import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ChevronDown, Plus, Check } from 'lucide-react';

const ACCENT = '#667eea';
const TEXT_TITLE = '#1a1a2e';
const TEXT_BODY = '#334155';
const TEXT_MUTED = '#64748b';
const TEXT_FAINT = '#94a3b8';
const BORDER = '#e2e8f0';
const SURFACE = '#ffffff';
const SURFACE_EMPTY = '#f1f5f9';
const SURFACE_EMPTY_WEEKEND = '#f8fafc';
const TODAY_RING = '#0f172a';
const PLANNED = '#fca5a5';      // soft red — planned, not checked in
const FUTURE_PLANNED = '#c7d2fe'; // light purple — planned future days
const ATTENDED = '#22c55e';     // green — planned + checked in
const DROPIN = '#86efac';       // light green — checked in, not planned


const SQUARE = 16;
const GAP = 3;
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Day-of-week as 0 (Mon) … 6 (Sun), matching the grid row order.
function isoWeekday(d) {
  return (d.getDay() + 6) % 7;
}

function todayIsoLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// "2026-11-23" → "Sunday, November 23 - 2026"
function formatDayTooltip(iso) {
  if (!iso) return '';
  const [y, m, day] = iso.split('-').map(Number);
  if (!y || !m || !day) return iso;
  const d = new Date(y, m - 1, day);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const monthName = d.toLocaleDateString(undefined, { month: 'long' });
  return `${weekday}, ${monthName} ${day} - ${y}`;
}

// Build the 53×7 grid for a given calendar year.
// Returns an array of cells, each: { date: 'YYYY-MM-DD' | null, col, row, isWeekend, isOutOfYear }
// Col 0 = the week that contains Jan 1; out-of-year leading days are null cells (transparent).
// Build the cells for one half of the year: half = 1 (Jan–Jun) or 2 (Jul–Dec).
// Each cell is { date, col, row, isWeekend, isOutOfYear, _jsDate }.
// isOutOfYear is true for cells outside the rendered half (used to render transparent placeholders
// in the edge weeks). The Square component checks only this flag to decide whether to render.
function buildGridCells(year, half) {
  const startMonth = half === 1 ? 0 : 6;   // 0 = Jan, 6 = Jul
  const endMonth = half === 1 ? 5 : 11;    // 5 = Jun, 11 = Dec
  const halfStart = new Date(year, startMonth, 1);
  const halfEnd = new Date(year, endMonth + 1, 0); // last day of endMonth

  // First column starts at the Monday of the week containing halfStart.
  const firstColStart = new Date(halfStart);
  firstColStart.setDate(firstColStart.getDate() - isoWeekday(halfStart));

  // Number of weekly columns needed to cover up through halfEnd.
  const lastColEnd = new Date(halfEnd);
  lastColEnd.setDate(lastColEnd.getDate() + (6 - isoWeekday(halfEnd)));
  const dayMs = 24 * 60 * 60 * 1000;
  const totalDays = Math.round((lastColEnd - firstColStart) / dayMs) + 1;
  const numWeeks = Math.ceil(totalDays / 7);

  const cells = [];
  for (let i = 0; i < numWeeks * 7; i++) {
    const d = new Date(firstColStart);
    d.setDate(d.getDate() + i);
    const col = Math.floor(i / 7);
    const row = i % 7;
    const inHalf = d >= halfStart && d <= halfEnd && d.getFullYear() === year;
    const iso = inHalf
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      : null;
    cells.push({
      date: iso,
      col,
      row,
      isWeekend: row >= 5,
      isOutOfYear: !inHalf, // generic "render transparent placeholder" flag
      _jsDate: d
    });
  }
  return cells;
}

// For each month in the half, find the column of its first day-of-month inside the grid.
function buildMonthLabelPositions(cells, year, half) {
  const startMonth = half === 1 ? 0 : 6;
  const endMonth = half === 1 ? 5 : 11;
  const positions = [];
  for (let m = startMonth; m <= endMonth; m++) {
    const firstCell = cells.find((c) => !c.isOutOfYear && c._jsDate.getFullYear() === year && c._jsDate.getMonth() === m && c._jsDate.getDate() === 1);
    if (firstCell) positions.push({ month: m, col: firstCell.col });
  }
  return positions;
}

function datesForWeekdayInYear(year, weekdayIndex) {
  // weekdayIndex: 0=Mon … 4=Fri
  const result = [];
  const d = new Date(year, 0, 1);
  while (d.getFullYear() === year) {
    if (isoWeekday(d) === weekdayIndex) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      result.push(iso);
    }
    d.setDate(d.getDate() + 1);
  }
  return result;
}

function datesForWeekOffset(weekOffset) {
  // 0 = this week (Mon–Fri), 1 = next week
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(monday.getDate() - isoWeekday(today) + weekOffset * 7);
  const result = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  return result;
}

function Office({ user, workspaceId }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const today = todayIsoLocal();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setUsers] = useState([]);
  const [viewedUserId, setViewedUserId] = useState(user.id);
  const canPickUsers = !!(user.is_admin || user.can_view_all_attendance);

  useEffect(() => {
    if (!canPickUsers) return;
    axios.get('/api/attendance/users').then((r) => setUsers(r.data)).catch(() => {});
  }, [canPickUsers]);

  const isViewingSelf = viewedUserId === user.id;

  // Right-panel carousel state + data
  const [backlogItems, setBacklogItems] = useState([]);
  const [backlogLoaded, setBacklogLoaded] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    setBacklogLoaded(false);
    axios.get('/api/backlog')
      .then((r) => setBacklogItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setBacklogLoaded(true));
  }, [workspaceId]);

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { year };
      if (viewedUserId !== user.id) params.user_id = viewedUserId;
      const res = await axios.get('/api/attendance', { params });
      setRows(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [year, viewedUserId, user.id]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  // Map: date string → row { planned, checked_in_at }
  const rowsByDate = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(r.date, r);
    return m;
  }, [rows]);

  const togglePlan = async (dateIso) => {
    if (!isViewingSelf) return;
    if (dateIso < today) return; // server will reject; bail early
    const existing = rowsByDate.get(dateIso);
    const nextPlanned = !(existing?.planned);

    // Optimistic
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.date === dateIso);
      if (idx === -1) return [...prev, { date: dateIso, planned: nextPlanned, checked_in_at: null }];
      const next = [...prev];
      next[idx] = { ...next[idx], planned: nextPlanned };
      return next;
    });

    try {
      const res = await axios.put('/api/attendance', { date: dateIso, planned: nextPlanned });
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.date === dateIso);
        const next = [...prev];
        if (idx === -1) next.push(res.data);
        else next[idx] = res.data;
        return next;
      });
    } catch (err) {
      // Rollback
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.date === dateIso);
        if (idx === -1) return prev;
        const next = [...prev];
        if (existing) next[idx] = existing;
        else next.splice(idx, 1);
        return next;
      });
      alert(err.response?.data?.error || 'Failed to save');
    }
  };

  const weekdayActive = useMemo(() => {
    const out = [false, false, false, false, false];
    for (let i = 0; i < 5; i++) {
      const dates = datesForWeekdayInYear(year, i).filter((d) => d >= today);
      if (dates.length === 0) { out[i] = false; continue; }
      out[i] = dates.every((d) => rowsByDate.get(d)?.planned);
    }
    return out;
  }, [year, rowsByDate, today]);

  const toggleWeekday = async (weekdayIndex) => {
    if (!isViewingSelf) return;
    const dates = datesForWeekdayInYear(year, weekdayIndex).filter((d) => d >= today);
    if (dates.length === 0) return;
    const nextPlanned = !weekdayActive[weekdayIndex];

    const before = new Map(rowsByDate);
    setRows((prev) => {
      const next = [...prev];
      for (const d of dates) {
        const idx = next.findIndex((r) => r.date === d);
        if (idx === -1) next.push({ date: d, planned: nextPlanned, checked_in_at: null });
        else next[idx] = { ...next[idx], planned: nextPlanned };
      }
      return next;
    });

    try {
      await axios.put('/api/attendance/bulk', { dates, planned: nextPlanned });
      await loadAttendance(); // re-fetch to get authoritative state
    } catch (err) {
      setRows(Array.from(before.values()));
      alert(err.response?.data?.error || 'Failed to apply preset');
    }
  };

  const planWeek = async (weekOffset) => {
    if (!isViewingSelf) return;
    const dates = datesForWeekOffset(weekOffset).filter((d) => d >= today);
    if (dates.length === 0) return;
    try {
      await axios.put('/api/attendance/bulk', { dates, planned: true });
      await loadAttendance();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to plan week');
    }
  };

  const [menuOpen, setMenuOpen] = useState(false);

  const todayRow = rowsByDate.get(today);
  const todayCheckedIn = !!todayRow?.checked_in_at;

  const formatCheckInTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  const checkInOn = async (dateIso) => {
    if (!isViewingSelf) return;
    try {
      const res = await axios.post('/api/attendance/check-in', { date: dateIso });
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.date === dateIso);
        const next = [...prev];
        if (idx === -1) next.push(res.data);
        else next[idx] = res.data;
        return next;
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to check in');
    } finally {
      setMenuOpen(false);
    }
  };

  const undoCheckIn = async (dateIso) => {
    if (!isViewingSelf) return;
    if (dateIso !== today) {
      if (!window.confirm('Undo this check-in?')) return;
    }
    try {
      const res = await axios.delete('/api/attendance/check-in', { data: { date: dateIso } });
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.date === dateIso);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = res.data;
        return next;
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to undo check-in');
    } finally {
      setMenuOpen(false);
    }
  };

  // Build the last 7 days for the dropdown menu
  const last7Days = useMemo(() => {
    const out = [];
    for (let i = 0; i <= 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      out.push({
        iso,
        label: i === 0
          ? 'Today'
          : i === 1
            ? 'Yesterday'
            : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
      });
    }
    return out;
  }, []);

  function isoWeekRange(d) {
    const start = new Date(d);
    start.setDate(start.getDate() - isoWeekday(start));
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const toIso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    return { startIso: toIso(start), endIso: toIso(end) };
  }

  const thisWeekStats = useMemo(() => {
    const { startIso, endIso } = isoWeekRange(new Date());
    let checkedIn = 0;
    for (const r of rows) {
      if (r.date >= startIso && r.date <= endIso && r.checked_in_at) checkedIn += 1;
    }
    return { checkedIn, target: 3 };
  }, [rows]);

  const ytdStats = useMemo(() => {
    // Expected: workdays from Jan 1 of the displayed year up to min(today, Dec 31 of year).
    // Counted: checked-in workdays in that range.
    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const todayD = new Date();
    const rangeEnd = todayD < yearEnd ? todayD : yearEnd;
    let expected = 0;
    const d = new Date(yearStart);
    while (d <= rangeEnd) {
      const wd = isoWeekday(d);
      if (wd < 5) expected += 1;
      d.setDate(d.getDate() + 1);
    }
    let attended = 0;
    let planned = 0;
    let dropIn = 0;
    let noShow = 0;
    for (const r of rows) {
      const rd = new Date(r.date + 'T00:00:00');
      const wd = isoWeekday(rd);
      const isWorkday = wd < 5;
      const isPast = r.date < todayIsoLocal();
      if (isWorkday && r.checked_in_at) attended += 1;
      if (isWorkday && r.planned) planned += 1;
      if (isWorkday && r.checked_in_at && !r.planned) dropIn += 1;
      if (isWorkday && r.planned && !r.checked_in_at && isPast) noShow += 1;
    }
    const pct = expected === 0 ? 0 : Math.round((attended / expected) * 100);
    return { expected, attended, planned, dropIn, noShow, pct };
  }, [rows, year]);

  // Monthly attended-day counts for the selected year (length 12, Jan-Dec).
  const monthlyAttended = useMemo(() => {
    const counts = new Array(12).fill(0);
    for (const r of rows) {
      if (!r.checked_in_at) continue;
      const rd = new Date(r.date + 'T00:00:00');
      if (rd.getFullYear() !== year) continue;
      if (isoWeekday(rd) >= 5) continue;
      counts[rd.getMonth()] += 1;
    }
    return counts;
  }, [rows, year]);

  // Monthly planned-day counts for the selected year (length 12, Jan-Dec).
  const monthlyPlanned = useMemo(() => {
    const counts = new Array(12).fill(0);
    for (const r of rows) {
      if (!r.planned) continue;
      const rd = new Date(r.date + 'T00:00:00');
      if (rd.getFullYear() !== year) continue;
      if (isoWeekday(rd) >= 5) continue;
      counts[rd.getMonth()] += 1;
    }
    return counts;
  }, [rows, year]);

  // This-month attended count (current calendar month, only if year == current year).
  const thisMonthAttended = useMemo(() => {
    const now = new Date();
    if (now.getFullYear() !== year) return 0;
    return monthlyAttended[now.getMonth()];
  }, [monthlyAttended, year]);

  // Best week in the selected year (highest checked-in workday count in any single ISO week).
  const bestWeekAttended = useMemo(() => {
    const byWeek = new Map();
    for (const r of rows) {
      if (!r.checked_in_at) continue;
      const rd = new Date(r.date + 'T00:00:00');
      if (rd.getFullYear() !== year) continue;
      if (isoWeekday(rd) >= 5) continue;
      const monday = new Date(rd);
      monday.setDate(monday.getDate() - isoWeekday(rd));
      const key = `${monday.getFullYear()}-${monday.getMonth()}-${monday.getDate()}`;
      byWeek.set(key, (byWeek.get(key) || 0) + 1);
    }
    let best = 0;
    for (const n of byWeek.values()) if (n > best) best = n;
    return best;
  }, [rows, year]);

  // Tasks (backlog items) for the person whose grid is being viewed.
  const taskStats = useMemo(() => {
    const mine = backlogItems.filter((b) => b.user_id === viewedUserId);
    const byStatus = mine.reduce((acc, b) => {
      acc[b.status || 'new'] = (acc[b.status || 'new'] || 0) + 1;
      return acc;
    }, {});
    const open = mine.filter((b) => b.status !== 'done' && b.status !== 'converted').length;
    const recent = [...mine]
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
      .slice(0, 5);
    return { total: mine.length, open, byStatus, recent };
  }, [backlogItems, viewedUserId]);

  const cellsH1 = useMemo(() => buildGridCells(year, 1), [year]);
  const cellsH2 = useMemo(() => buildGridCells(year, 2), [year]);
  const monthPositionsH1 = useMemo(() => buildMonthLabelPositions(cellsH1, year, 1), [cellsH1, year]);
  const monthPositionsH2 = useMemo(() => buildMonthLabelPositions(cellsH2, year, 2), [cellsH2, year]);

  return (
    <div data-fullwidth="true" style={{ padding: '24px 32px 32px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 600, color: TEXT_TITLE, fontStyle: 'italic' }}>
            Office
          </h1>
          <p style={{ margin: '4px 0 0', color: TEXT_MUTED, fontSize: '0.9rem' }}>
            Plan and check in for office days.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 14px',
            background: thisWeekStats.checkedIn >= thisWeekStats.target ? ACCENT : SURFACE,
            color: thisWeekStats.checkedIn >= thisWeekStats.target ? 'white' : TEXT_BODY,
            border: thisWeekStats.checkedIn >= thisWeekStats.target ? 'none' : `1px solid ${BORDER}`,
            borderRadius: '999px',
            fontSize: '0.85rem',
            fontWeight: 600
          }}>
            {thisWeekStats.checkedIn} / {thisWeekStats.target} this week
            {thisWeekStats.checkedIn >= thisWeekStats.target && <Check size={14} />}
          </div>

          {/* Year selector */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: '999px',
            padding: '4px 6px'
          }}>
            <button
              type="button"
              onClick={() => setYear((y) => Math.max(y - 1, currentYear - 5))}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', color: TEXT_MUTED }}
              disabled={year <= currentYear - 5}
              title="Previous year"
            >
              <ChevronLeft size={16} />
            </button>
            <span style={{ minWidth: '54px', textAlign: 'center', fontWeight: 600, color: TEXT_TITLE }}>{year}</span>
            <button
              type="button"
              onClick={() => setYear((y) => Math.min(y + 1, currentYear + 5))}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', color: TEXT_MUTED }}
              disabled={year >= currentYear + 5}
              title="Next year"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Check-in CTA (wired in Task 13) */}
          <div style={{ opacity: isViewingSelf ? 1 : 0.4, pointerEvents: isViewingSelf ? 'auto' : 'none' }}>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => todayCheckedIn ? undoCheckIn(today) : checkInOn(today)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: todayCheckedIn ? SURFACE : ACCENT,
                  color: todayCheckedIn ? TEXT_BODY : 'white',
                  border: todayCheckedIn ? `1px solid ${BORDER}` : 'none',
                  borderRadius: '24px 0 0 24px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {todayCheckedIn ? <Check size={16} /> : <Plus size={16} />}
                {todayCheckedIn ? `Checked in ${formatCheckInTime(todayRow.checked_in_at)}` : 'Check in today'}
              </button>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                style={{
                  padding: '10px 12px',
                  background: todayCheckedIn ? SURFACE : ACCENT,
                  color: todayCheckedIn ? TEXT_BODY : 'white',
                  border: todayCheckedIn ? `1px solid ${BORDER}` : 'none',
                  borderLeft: todayCheckedIn ? `1px solid ${BORDER}` : '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '0 24px 24px 0',
                  cursor: 'pointer'
                }}
                title="Check in for another day"
              >
                <ChevronDown size={16} />
              </button>

              {menuOpen && (
                <CheckInMenu
                  days={last7Days}
                  rowsByDate={rowsByDate}
                  onCheckIn={checkInOn}
                  onUndo={undoCheckIn}
                  onClose={() => setMenuOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div style={{
          height: '8px',
          background: BORDER,
          borderRadius: '999px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(100, ytdStats.pct)}%`,
            background: ACCENT,
            transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: TEXT_MUTED }}>
          <span>3 days/week target</span>
          <span>{ytdStats.attended} / {ytdStats.expected} expected days — {ytdStats.pct}%</span>
        </div>
      </div>

      {canPickUsers && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: TEXT_MUTED, fontSize: '0.85rem', fontWeight: 500 }}>Viewing</span>
          <select
            value={viewedUserId}
            onChange={(e) => setViewedUserId(parseInt(e.target.value, 10))}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${BORDER}`,
              background: SURFACE,
              fontSize: '0.9rem',
              color: TEXT_BODY,
              cursor: 'pointer'
            }}
          >
            <option value={user.id}>Your attendance</option>
            {users.filter((u) => u.id !== user.id).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          {!isViewingSelf && (
            <span style={{ fontSize: '0.78rem', color: TEXT_FAINT }}>Read-only — viewing someone else's grid</span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', opacity: isViewingSelf ? 1 : 0.4, pointerEvents: isViewingSelf ? 'auto' : 'none' }}>
        <span style={{ color: TEXT_MUTED, fontSize: '0.85rem', fontWeight: 500 }}>Plan all</span>
        {WEEKDAY_LABELS.slice(0, 5).map((lbl, i) => {
          const active = weekdayActive[i];
          return (
            <button
              key={lbl}
              type="button"
              onClick={() => toggleWeekday(i)}
              style={{
                padding: '6px 14px',
                background: active ? ACCENT : SURFACE,
                color: active ? 'white' : TEXT_BODY,
                border: active ? 'none' : `1px solid ${BORDER}`,
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {lbl}
            </button>
          );
        })}
        <span style={{ width: '1px', height: '20px', background: BORDER, margin: '0 4px' }} />
        <button
          type="button"
          onClick={() => planWeek(0)}
          style={{
            padding: '6px 14px',
            background: SURFACE,
            color: TEXT_BODY,
            border: `1px solid ${BORDER}`,
            borderRadius: '999px',
            fontSize: '0.8rem',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          Plan this week
        </button>
        <button
          type="button"
          onClick={() => planWeek(1)}
          style={{
            padding: '6px 14px',
            background: SURFACE,
            color: TEXT_BODY,
            border: `1px solid ${BORDER}`,
            borderRadius: '999px',
            fontSize: '0.8rem',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          Plan next week
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#b91c1c',
          borderRadius: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button type="button" onClick={loadAttendance} style={{
            padding: '4px 12px', background: 'white', border: '1px solid #fecaca',
            borderRadius: '6px', color: '#b91c1c', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer'
          }}>Retry</button>
        </div>
      )}

      {/* Two-column layout: planning grid on the left, carousel on the right */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'stretch', flexWrap: 'wrap' }}>
        {/* Grid — split into two halves (Jan–Jun on top, Jul–Dec below) */}
        <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '28px' }}>
          <div>
            <h2 style={{ margin: '0 0 10px', fontSize: '1rem', fontWeight: 600, fontStyle: 'italic', color: TEXT_TITLE }}>
              First half
            </h2>
            <AttendanceGrid
              cells={cellsH1}
              monthPositions={monthPositionsH1}
              today={today}
              rowsByDate={rowsByDate}
              onTogglePlan={togglePlan}
              loading={loading}
            />
          </div>
          <div>
            <h2 style={{ margin: '0 0 10px', fontSize: '1rem', fontWeight: 600, fontStyle: 'italic', color: TEXT_TITLE }}>
              Second half
            </h2>
            <AttendanceGrid
              cells={cellsH2}
              monthPositions={monthPositionsH2}
              today={today}
              rowsByDate={rowsByDate}
              onTogglePlan={togglePlan}
              loading={loading}
            />
          </div>
        </div>

        {/* Right-side carousel: stats + tasks summary */}
        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex' }}>
          <Carousel
            slideIndex={slideIndex}
            onChange={setSlideIndex}
            slides={[
              {
                title: isViewingSelf ? 'Your stats' : `${(users.find((u) => u.id === viewedUserId)?.name || 'User')}'s stats`,
                render: () => (
                  <StatsSlide
                    thisWeekStats={thisWeekStats}
                    ytdStats={ytdStats}
                    thisMonthAttended={thisMonthAttended}
                    bestWeekAttended={bestWeekAttended}
                    monthlyAttended={monthlyAttended}
                    monthlyPlanned={monthlyPlanned}
                  />
                )
              },
              {
                title: isViewingSelf ? 'Your tasks' : `${(users.find((u) => u.id === viewedUserId)?.name || 'User')}'s tasks`,
                render: () => (
                  <TasksSlide taskStats={taskStats} loaded={backlogLoaded} />
                )
              }
            ]}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', fontSize: '0.8rem', color: TEXT_MUTED }}>
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <LegendSwatch color={SURFACE_EMPTY} label="Empty" />
          <LegendSwatch color={FUTURE_PLANNED} label="Planned" />
          <LegendSwatch color={PLANNED} label="No-show" />
          <LegendSwatch color={ATTENDED} label="Attended" />
          <LegendSwatch color={DROPIN} label="Drop-in" />
          <LegendSwatch color="transparent" label="Today" ring />
        </div>
        <div>
          Year so far: {ytdStats.planned} planned · {ytdStats.attended} attended · {ytdStats.dropIn} drop-ins · {ytdStats.noShow} no-shows
        </div>
      </div>
    </div>
  );
}

function AttendanceGrid({ cells, monthPositions, today, rowsByDate, onTogglePlan, loading }) {
  const colCount = cells.length > 0 ? cells[cells.length - 1].col + 1 : 0;
  const gridWidth = colCount * SQUARE + (colCount - 1) * GAP;

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ position: 'relative', paddingLeft: '34px', paddingTop: '20px', minWidth: gridWidth + 34 }}>
        {/* Month labels */}
        <div style={{ position: 'absolute', top: 0, left: '34px', height: '16px', width: gridWidth }}>
          {monthPositions.map(({ month, col }) => (
            <span
              key={month}
              style={{
                position: 'absolute',
                left: col * (SQUARE + GAP),
                fontSize: '0.7rem',
                color: TEXT_MUTED,
                fontWeight: 500
              }}
            >
              {MONTH_LABELS[month]}
            </span>
          ))}
        </div>

        {/* Weekday labels (left column) */}
        <div style={{ position: 'absolute', left: 0, top: '20px', width: '30px' }}>
          {WEEKDAY_LABELS.map((lbl, i) => (
            <div
              key={lbl}
              style={{
                height: SQUARE,
                marginBottom: i < WEEKDAY_LABELS.length - 1 ? GAP : 0,
                fontSize: '0.65rem',
                color: TEXT_FAINT,
                lineHeight: `${SQUARE}px`,
                textAlign: 'right',
                paddingRight: '6px'
              }}
            >
              {lbl}
            </div>
          ))}
        </div>

        {/* Squares */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${colCount}, ${SQUARE}px)`,
          gridTemplateRows: `repeat(7, ${SQUARE}px)`,
          gridAutoFlow: 'column',
          gap: `${GAP}px`,
          width: gridWidth
        }}>
          {cells.map((cell) => (
            <Square
              key={`${cell.col}-${cell.row}`}
              cell={cell}
              today={today}
              row={cell.date ? rowsByDate.get(cell.date) : null}
              onTogglePlan={onTogglePlan}
              loading={loading}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Square({ cell, today, row, onTogglePlan, loading }) {
  if (cell.isOutOfYear) {
    return <div style={{ width: SQUARE, height: SQUARE, background: 'transparent' }} />;
  }

  const isToday = cell.date === today;
  const isPast = cell.date < today;
  const planned = !!row?.planned;
  const checkedIn = !!row?.checked_in_at;

  let bg;
  if (checkedIn && planned) bg = ATTENDED;
  else if (checkedIn && !planned) bg = DROPIN;
  else if (planned) bg = isPast ? PLANNED : FUTURE_PLANNED;
  else bg = cell.isWeekend ? SURFACE_EMPTY_WEEKEND : SURFACE_EMPTY;

  const clickable = !isPast && !loading;
  const tooltipState = checkedIn && planned
    ? 'Attended'
    : checkedIn && !planned
      ? 'Drop-in (checked in, not planned)'
      : planned
        ? (isPast ? 'No-show (planned, not checked in)' : 'Planned')
        : isPast ? 'Not attended' : 'Empty';

  return (
    <div
      role="button"
      tabIndex={clickable ? 0 : -1}
      onClick={clickable ? () => onTogglePlan(cell.date) : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTogglePlan(cell.date); } } : undefined}
      title={`${formatDayTooltip(cell.date)} — ${tooltipState}`}
      style={{
        width: SQUARE,
        height: SQUARE,
        background: bg,
        borderRadius: '2px',
        opacity: cell.isWeekend ? 0.55 : (loading ? 0.6 : 1),
        outline: isToday ? `2px solid ${TODAY_RING}` : 'none',
        outlineOffset: isToday ? '1px' : '0',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform 0.1s'
      }}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.transform = 'scale(1.4)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.transform = 'scale(1)'; } : undefined}
    />
  );
}

function LegendSwatch({ color, label, ring }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span style={{
        display: 'inline-block',
        width: '12px',
        height: '12px',
        background: color,
        borderRadius: '2px',
        outline: ring ? `2px solid ${TODAY_RING}` : 'none',
        outlineOffset: ring ? '1px' : '0',
        border: color === 'transparent' ? 'none' : `1px solid ${BORDER}`
      }} />
      {label}
    </span>
  );
}

function CheckInMenu({ days, rowsByDate, onCheckIn, onUndo, onClose }) {
  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 100 }}
        onClick={onClose}
      />
      <div style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        right: 0,
        zIndex: 101,
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        minWidth: '260px',
        padding: '6px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ padding: '6px 10px 8px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: TEXT_MUTED, fontWeight: 600 }}>
          Check in for a day
        </div>
        {days.map(({ iso, label }) => {
          const row = rowsByDate.get(iso);
          const isCheckedIn = !!row?.checked_in_at;
          return (
            <div
              key={iso}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 10px',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: TEXT_BODY
              }}
            >
              <span>{label} <span style={{ color: TEXT_FAINT, marginLeft: '6px' }}>{iso}</span></span>
              {isCheckedIn ? (
                <button
                  type="button"
                  onClick={() => onUndo(iso)}
                  style={{ padding: '3px 8px', fontSize: '0.75rem', background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: '999px', cursor: 'pointer', color: TEXT_MUTED }}
                >
                  Undo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onCheckIn(iso)}
                  style={{ padding: '3px 10px', fontSize: '0.75rem', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', cursor: 'pointer', fontWeight: 600 }}
                >
                  Check in
                </button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────
   Carousel — wraps two slides with dot nav + arrow
   ────────────────────────────────────────────── */
function Carousel({ slideIndex, onChange, slides }) {
  const active = slides[slideIndex] || slides[0];
  const count = slides.length;

  const goPrev = () => onChange((slideIndex - 1 + count) % count);
  const goNext = () => onChange((slideIndex + 1) % count);

  return (
    <div style={{
      flex: 1,
      background: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: '12px',
      padding: '20px 22px',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '320px'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, fontStyle: 'italic', color: TEXT_TITLE }}>
          {active.title}
        </h2>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={goPrev}
            disabled={count <= 1}
            title="Previous"
            style={{
              padding: '4px',
              background: 'transparent',
              border: 'none',
              cursor: count > 1 ? 'pointer' : 'default',
              color: TEXT_MUTED,
              display: 'flex'
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <div style={{ display: 'inline-flex', gap: '6px' }}>
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onChange(i)}
                aria-label={`Show slide ${i + 1}`}
                style={{
                  width: i === slideIndex ? '18px' : '6px',
                  height: '6px',
                  borderRadius: '999px',
                  background: i === slideIndex ? ACCENT : BORDER,
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'width 0.2s'
                }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={goNext}
            disabled={count <= 1}
            title="Next"
            style={{
              padding: '4px',
              background: 'transparent',
              border: 'none',
              cursor: count > 1 ? 'pointer' : 'default',
              color: TEXT_MUTED,
              display: 'flex'
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Slide body */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {active.render()}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Stats slide — KPI tiles + monthly bar chart
   ────────────────────────────────────────────── */
function StatsSlide({ thisWeekStats, ytdStats, thisMonthAttended, bestWeekAttended, monthlyAttended, monthlyPlanned = new Array(12).fill(0) }) {
  const monthLabels = MONTH_LABELS;
  const maxVal = Math.max(1, ...monthlyAttended, ...monthlyPlanned);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', height: '100%' }}>
      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
        <KpiTile label="This week" value={`${thisWeekStats.checkedIn}/${thisWeekStats.target}`} hint="days" />
        <KpiTile label="This month" value={thisMonthAttended} hint="attended" />
        <KpiTile label="YTD" value={`${ytdStats.pct}%`} hint={`${ytdStats.attended} of ${ytdStats.expected}`} />
        <KpiTile label="Best week" value={bestWeekAttended} hint="this year" />
      </div>

      {/* Monthly bar chart */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Monthly Planned vs Attended
          </span>
          <div style={{ display: 'flex', gap: '10px', fontSize: '0.65rem', fontWeight: 500 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', background: ACCENT, borderRadius: '1px' }} />
              Planned
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '8px', height: '8px', background: ATTENDED, borderRadius: '1px' }} />
              Attended
            </span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', alignItems: 'end', gap: '6px', height: '110px' }}>
          {monthLabels.map((m, i) => {
            const plannedCount = monthlyPlanned[i];
            const attendedCount = monthlyAttended[i];
            const plannedPct = Math.round((plannedCount / maxVal) * 100);
            const attendedPct = Math.round((attendedCount / maxVal) * 100);

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  gap: '2px',
                  height: '100%'
                }}
                title={`${m}: ${plannedCount} planned, ${attendedCount} attended`}
              >
                {/* Planned bar */}
                <div style={{
                  flex: 1,
                  height: `${Math.max(2, plannedPct)}%`,
                  background: plannedCount > 0 ? ACCENT : BORDER,
                  borderRadius: '2px 2px 0 0',
                  transition: 'height 0.3s'
                }} />
                {/* Attended bar */}
                <div style={{
                  flex: 1,
                  height: `${Math.max(2, attendedPct)}%`,
                  background: attendedCount > 0 ? ATTENDED : BORDER,
                  borderRadius: '2px 2px 0 0',
                  transition: 'height 0.3s'
                }} />
              </div>
            );
          })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '6px', marginTop: '6px' }}>
          {monthLabels.map((m) => (
            <div key={m} style={{ fontSize: '0.65rem', color: TEXT_FAINT, textAlign: 'center' }}>{m}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, hint }) {
  return (
    <div style={{
      padding: '12px 14px',
      background: '#f8fafc',
      border: `1px solid ${BORDER}`,
      borderRadius: '10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: TEXT_TITLE, lineHeight: 1.1 }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: '0.72rem', color: TEXT_FAINT }}>{hint}</div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────
   Tasks slide — counts + recent items
   ────────────────────────────────────────────── */
const TASK_STATUS_LABELS = {
  requested: 'Requested',
  new: 'New',
  triaged: 'Triaged',
  planned: 'Planned',
  'in-progress': 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  converted: 'Converted'
};

const TASK_STATUS_COLORS = {
  requested:   { bg: '#f1f5f9', fg: '#475569' },
  new:         { bg: '#eef2ff', fg: '#4338ca' },
  triaged:     { bg: '#f0f4ff', fg: '#3949ab' },
  planned:     { bg: '#dbeafe', fg: '#1d4ed8' },
  'in-progress': { bg: '#fef3c7', fg: '#b45309' },
  blocked:     { bg: '#fee2e2', fg: '#b91c1c' },
  done:        { bg: '#dcfce7', fg: '#15803d' },
  converted:   { bg: '#ede9fe', fg: '#6d28d9' }
};

function TasksSlide({ taskStats, loaded }) {
  if (!loaded) {
    return <div style={{ padding: '30px 0', color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading tasks…</div>;
  }
  if (taskStats.total === 0) {
    return (
      <div style={{ padding: '30px 0', color: TEXT_MUTED, fontSize: '0.85rem' }}>
        No tasks assigned yet.
      </div>
    );
  }

  const statusEntries = Object.entries(taskStats.byStatus)
    .filter(([s]) => s !== 'done' && s !== 'converted')
    .sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
      {/* Top summary */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
        <span style={{ fontSize: '1.8rem', fontWeight: 700, color: TEXT_TITLE, lineHeight: 1 }}>
          {taskStats.open}
        </span>
        <span style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>
          open · {taskStats.total} total
        </span>
      </div>

      {/* Status chips */}
      {statusEntries.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {statusEntries.map(([status, count]) => {
            const c = TASK_STATUS_COLORS[status] || TASK_STATUS_COLORS.new;
            return (
              <span key={status} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: c.bg,
                color: c.fg,
                padding: '3px 10px',
                borderRadius: '999px',
                fontSize: '0.75rem',
                fontWeight: 600
              }}>
                {TASK_STATUS_LABELS[status] || status} · {count}
              </span>
            );
          })}
        </div>
      )}

      {/* Recent items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minHeight: 0, overflowY: 'auto' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Recent
        </div>
        {taskStats.recent.map((item) => {
          const c = TASK_STATUS_COLORS[item.status] || TASK_STATUS_COLORS.new;
          return (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                padding: '8px 10px',
                background: '#f8fafc',
                border: `1px solid ${BORDER}`,
                borderRadius: '8px',
                fontSize: '0.85rem'
              }}
            >
              <span style={{ color: TEXT_BODY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                {item.title}
              </span>
              <span style={{
                background: c.bg,
                color: c.fg,
                padding: '2px 8px',
                borderRadius: '999px',
                fontSize: '0.7rem',
                fontWeight: 600,
                flexShrink: 0
              }}>
                {TASK_STATUS_LABELS[item.status] || item.status}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Office;
